import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { DialogActions } from "ui/dialog";
import { Select, type OptionType } from "ui/select";
import { Switch } from "ui/switch";

import { DEFAULT_PROGRAM_TYPE, useGetTouchpointContentTypesQuery } from "hooks/api/dictionary";

import { useCampaignFromTemplateContext } from "components/modals/create-campaign-from-template-modal/context/campaign-from-template-context";
import { useCampaignTemplateData } from "components/modals/create-campaign-from-template-modal/hooks/use-campaign-template-data";
import type { CreateCampaignFromTemplateFormData } from "components/modals/create-campaign-from-template-modal/create-campaign-from-template-modal.types";
import TouchpointIcon from "containers/admin/clients/touchpoint/components/touchpoint-icon/touchpoint-icon";

import { MondayTouchpointTemplateDTO } from "generated/schemas";

import { toSpacedTitleCase } from "utilities";
import { getTouchpointTypeFromMondayChannel } from "utilities/monday-channel";

import Icons from "assets";

const FIELDS_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const CampaignTemplateSelectionStep = () => {
  const {
    strategies,
    isLoadingStrategies,
    campaignTemplates,
    isLoadingCampaignTemplates,
    campaignObjectives,
    isLoadingCampaignObjectives,
    programTypes,
    setInvalidTemplateIds,
  } = useCampaignFromTemplateContext();

  const { control, watch, setValue } = useFormContext<CreateCampaignFromTemplateFormData>();

  const selectedStrategy = watch("strategy");
  const selectedProgramType = watch("programType");
  const selectedCampaignObjective = watch("campaignObjective");
  const selectedCampaignTemplate = watch("campaignTemplates");
  const selectedTransferStudentStatusFlag = watch("transferStudentStatusFlag");
  const isTransferCampaignDisabled = selectedProgramType !== DEFAULT_PROGRAM_TYPE;

  // Use the hook to handle all data fetching
  useCampaignTemplateData(
    selectedStrategy,
    selectedProgramType,
    selectedCampaignObjective,
    selectedTransferStudentStatusFlag,
  );

  const { data: validContentTypes = [] } = useGetTouchpointContentTypesQuery(
    selectedCampaignObjective || undefined,
  );

  const validContentTypeDescriptions = useMemo(() => {
    return new Set(validContentTypes.map((ct) => ct.description));
  }, [validContentTypes]);

  const resetDependentFields = () => {
    setValue("campaignObjective", "");
    setValue("campaignTemplates", []);
  };

  const handleProgramTypeChange = (onChange: (value: string | undefined) => void) => {
    return (selectedOption: OptionType | null) => {
      const programType = selectedOption?.value as string | undefined;
      onChange(programType);
      setValue("transferStudentStatusFlag", false);
      resetDependentFields();
    };
  };

  const getStrategyPlaceholder = (): string => {
    if (isLoadingStrategies) {
      return "Loading strategies...";
    }
    return "";
  };

  const getCampaignObjectivePlaceholder = (): string => {
    if (isLoadingCampaignObjectives) {
      return "Loading objectives...";
    }
    if (selectedProgramType && selectedStrategy && !campaignObjectives.length) {
      return "No campaign objectives available for this combination";
    }
    return "";
  };

  const getCampaignObjectiveHelperText = (): string => {
    if (
      selectedProgramType &&
      selectedStrategy &&
      !isLoadingCampaignObjectives &&
      !campaignObjectives.length
    ) {
      return "No objectives match this selection. Try adjusting program type or strategy.";
    }
    return "Available after strategy and program type are selected.";
  };
  const getCampaignTemplatesPlaceholder = (): string => {
    if (isLoadingCampaignTemplates) {
      return "Loading templates...";
    }
    return "";
  };

  const updateTemplatesForSelectedCampaignTemplates = (
    selectedCampaignTemplateIds: (string | number)[],
    validContentTypes: Set<string>,
  ) => {
    if (selectedCampaignTemplateIds.length) {
      // Get all valid touchpoint template IDs from the newly selected campaign templates
      const validTouchpointTemplateIds = new Set<string>();
      const touchpointTemplatesMap = new Map<string, MondayTouchpointTemplateDTO>();

      selectedCampaignTemplateIds.forEach((campaignTemplateId: string | number) => {
        const campaignTemplate = campaignTemplates.find((ct) => ct.id === campaignTemplateId);
        campaignTemplate?.touchpointTemplates?.forEach((touchpointTemplate) => {
          if (touchpointTemplate.id) {
            validTouchpointTemplateIds.add(touchpointTemplate.id);
            touchpointTemplatesMap.set(touchpointTemplate.id, touchpointTemplate);
          }
        });
      });

      // Get current templates object
      const currentTemplateSelections = watch("templates") || {};

      // Helper function to check if template is valid
      const isContentTypeValid = (touchpointTemplate: MondayTouchpointTemplateDTO): boolean => {
        return (
          !touchpointTemplate.contentType || validContentTypes.has(touchpointTemplate.contentType)
        );
      };

      // Build new templates object: keep existing valid values, add new ones with default true, set invalid to false
      // Also collect invalid template IDs to store in context
      const invalidIds = new Set<string>();
      const updatedTemplates = Array.from(validTouchpointTemplateIds).reduce(
        (templateSelections, touchpointTemplateId) => {
          const currentTouchpointTemplate = touchpointTemplatesMap.get(touchpointTemplateId);
          if (!currentTouchpointTemplate) return templateSelections;

          // Check if template is valid based on content type
          const hasValidContentType = isContentTypeValid(currentTouchpointTemplate);

          // If invalid, always set to false and add to invalid IDs
          if (!hasValidContentType) {
            templateSelections[touchpointTemplateId] = false;
            invalidIds.add(touchpointTemplateId);
          } else {
            // Preserve existing value if it exists, otherwise default to true
            templateSelections[touchpointTemplateId] =
              currentTemplateSelections[touchpointTemplateId] ?? true;
          }
          return templateSelections;
        },
        {} as Record<string, boolean>,
      );
      // Set the updated templates object
      setValue("templates", updatedTemplates, { shouldValidate: false });
      // Store invalid template IDs in context for use in step-2
      setInvalidTemplateIds(invalidIds);
    } else {
      // If no campaign templates selected, clear all template selections
      setValue("templates", {}, { shouldValidate: false });
      // Clear invalid template IDs
      setInvalidTemplateIds(new Set());
    }
  };

  useEffect(() => {
    if (!!programTypes.length && !selectedProgramType) {
      const defaultProgramType = programTypes.find((pt) => pt.name === "AWARE_TO_APPLY");
      if (defaultProgramType) {
        setValue("programType", defaultProgramType.name);
      }
    }
  }, [programTypes, selectedProgramType, setValue]);

  const renderSelectedTemplates = () => {
    if (!selectedCampaignTemplate?.length) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2 mb-4">
        <p className="">Selected campaign templates:</p>
        <div className="flex flex-col gap-2">
          {selectedCampaignTemplate.map((templateId: string) => {
            const template = campaignTemplates.find((t) => t.id === templateId);
            if (!template) {
              return null;
            }
            const populationTypes = [
              ...new Set(
                template.touchpointTemplates
                  ?.map((touchpoint) => touchpoint.populationType)
                  .filter(Boolean) ?? [],
              ),
            ];
            const populationLabel = populationTypes
              .map((populationType) => toSpacedTitleCase(populationType))
              .join(", ");

            return (
              <div className="flex items-center gap-2" key={templateId}>
                <div className="flex items-center gap-1">
                  <TouchpointIcon
                    touchpointType={getTouchpointTypeFromMondayChannel(
                      template.campaignChannel ??
                        template.touchpointTemplates?.[0]?.touchpointChannel,
                    )}
                    className="size-4 shrink-0"
                  />
                  <p className="text-sm">{template.campaignTemplate}</p>
                </div>
                {populationLabel && (
                  <span className="text-sm text-gray-500">{populationLabel}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-gray-600">Please choose campaign template from existing templates.</p>
        <Box sx={FIELDS_SX}>
          <Controller
            key="strategy"
            name="strategy"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const strategyOptions = strategies.map((strategy) => ({
                label: strategy.strategy,
                value: strategy.id || "",
              }));

              return (
                <Select
                  label={<span className="font-bold">Strategy</span>}
                  selectName="strategy"
                  placeholder={getStrategyPlaceholder()}
                  required
                  options={strategyOptions}
                  ariaLabel="Select strategy"
                  value={strategyOptions.find((option) => option.value === value) || null}
                  onChange={(selectedOption) => {
                    onChange(selectedOption?.value);
                    resetDependentFields();
                  }}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                  {...field}
                />
              );
            }}
          />
          <Controller
            key="programType"
            name="programType"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const programTypeOptions = programTypes.map((programType) => ({
                label: programType.description,
                value: programType.name,
              }));

              return (
                <Select
                  label={<span className="font-bold">Program type</span>}
                  selectName="programType"
                  required
                  options={programTypeOptions}
                  ariaLabel="Select program type"
                  value={programTypeOptions.find((option) => option.value === value) || null}
                  onChange={handleProgramTypeChange(onChange)}
                  {...field}
                />
              );
            }}
          />
          <Controller
            key="transferStudentStatusFlag"
            name="transferStudentStatusFlag"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch
                toggleName="Transfer campaign"
                checked={Boolean(value)}
                onChange={onChange}
                disabled={isTransferCampaignDisabled}
              />
            )}
          />
          <Controller
            key="campaignObjective"
            name="campaignObjective"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const campaignObjectiveOptions = campaignObjectives.map((objective) => ({
                label: objective.description,
                value: objective.name,
              }));

              return (
                <Select
                  label={<span className="font-bold">Campaign objective</span>}
                  selectName="campaignObjective"
                  placeholder={getCampaignObjectivePlaceholder()}
                  helperText={getCampaignObjectiveHelperText()}
                  required
                  disabled={
                    !selectedProgramType ||
                    !selectedStrategy ||
                    isLoadingCampaignObjectives ||
                    !campaignObjectives.length
                  }
                  options={campaignObjectiveOptions}
                  ariaLabel="Select campaign objective"
                  value={campaignObjectiveOptions.find((option) => option.value === value) || null}
                  onChange={(selectedOption) => {
                    onChange(selectedOption?.value);
                    setValue("campaignTemplates", []);
                  }}
                  {...field}
                />
              );
            }}
          />

          <Controller
            key="campaignTemplates"
            name="campaignTemplates"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              const templateOptions: OptionType[] = campaignTemplates.map((template) => ({
                label: template.campaignTemplate,
                value: template.id || "",
              }));
              const selectedOptions = Array.isArray(value)
                ? value
                    .map((val) => templateOptions.find((opt) => opt.value === val))
                    .filter((opt) => opt !== undefined)
                : [];

              return (
                <Select
                  label={<span className="font-bold">Campaign templates</span>}
                  selectName="campaignTemplates"
                  placeholder={getCampaignTemplatesPlaceholder()}
                  required
                  multiple
                  disabled={
                    !selectedStrategy || !selectedCampaignObjective || isLoadingCampaignTemplates
                  }
                  helperText="Available after campaign objective is selected."
                  options={templateOptions}
                  ariaLabel="Select campaign templates"
                  value={selectedOptions}
                  onChange={(selected) => {
                    const values = selected.map((option) => option.value as string);
                    onChange(values);
                    updateTemplatesForSelectedCampaignTemplates(
                      values,
                      validContentTypeDescriptions,
                    );
                  }}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                  {...field}
                />
              );
            }}
          />

          {renderSelectedTemplates()}
        </Box>
      </div>
    </>
  );
};

export const CampaignTemplateSelectionStepActions = () => {
  const { campaignObjectives, next, setIsOpen } = useCampaignFromTemplateContext();
  const { watch } = useFormContext<CreateCampaignFromTemplateFormData>();

  const [selectedStrategy, selectedProgramType, selectedCampaignTemplate] = watch([
    "strategy",
    "programType",
    "campaignTemplates",
  ]);
  const isContinueDisabled = Boolean(
    !selectedCampaignTemplate?.length ||
    (selectedProgramType && selectedStrategy && !campaignObjectives.length),
  );

  return (
    <DialogActions
      type="steps"
      secondary={
        <Button size="large" variant="outlined" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
      }
      primary={
        <Button size="large" disabled={isContinueDisabled} onClick={next}>
          Continue with details
        </Button>
      }
    />
  );
};

export default CampaignTemplateSelectionStep;
